/**
 * Sentry инициализация для клиента
 * Чеклист:
 * ✅ Инициализировать Sentry с DSN, environment и integrations
 * ✅ Настроить фильтрацию ошибок (ignoreErrors, beforeSend)
 */
import * as Sentry from '@sentry/vue';

export default defineNuxtPlugin((nuxtApp) => {
  // Работаем только на клиенте
  if (typeof window === 'undefined') {
    console.log('[Sentry] Пропуск инициализации - не на клиенте');
    return;
  }

  const runtimeConfig = useRuntimeConfig();
  const dsn = runtimeConfig.public.sentryDsn as string;

  // Инициализируем Sentry только если DSN указан
  if (!dsn) {
    console.warn('[Sentry] DSN не указан, Sentry отключен');
    return;
  }

  try {
    Sentry.init({
      app: nuxtApp.vueApp,
      dsn,
      environment: process.env.NODE_ENV || 'production',

      // Отслеживание производительности
      tracesSampleRate: 0, // Отключено для экономии квоты

      // Фильтрация известных шумовых ошибок
      ignoreErrors: [
        'Non-Error promise rejection captured',
        'ResizeObserver loop limit exceeded',
        'Network request failed',
        'Failed to fetch',
        // Игнорируем некоторые браузерные ошибки
        /^Non-Error/,
      ],

      // beforeSend - последняя линия защиты, здесь можно дополнительно фильтровать
      beforeSend(event, hint) {
        // Игнорируем ошибки из расширений браузера
        if (event.exception) {
          const frames = event.exception.values?.[0]?.stacktrace?.frames;
          if (
            frames?.some(
              (frame) =>
                frame.filename?.includes('chrome-extension://') ||
                frame.filename?.includes('moz-extension://')
            )
          ) {
            return null; // Игнорируем ошибки из расширений
          }
        }

        return event;
      },

      // Интеграции
      integrations: [
        Sentry.replayIntegration({
          // Session Replay для воспроизведения ошибок
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],

      // Session Replay настройки
      replaysSessionSampleRate: 0, // Отключено для экономии квоты
      replaysOnErrorSampleRate: 0.1, // 10% сессий с ошибками записываются
    });

    // Добавляем глобальную функцию для тестирования (только в development)
    if (process.env.NODE_ENV !== 'production') {
      try {
        if (typeof window !== 'undefined') {
          (window as any).testSentry = () => {
            console.log('[Sentry Test] Отправка тестовых сообщений...');

            const msgId = Sentry.captureMessage('Test message from window.testSentry()', 'info');
            console.log('[Sentry Test] Message Event ID:', msgId);

            const errId = Sentry.captureException(new Error('Test error from window.testSentry()'));
            console.log('[Sentry Test] Error Event ID:', errId);

            Sentry.flush(2000).then(() => {
              console.log('[Sentry Test] ✅ Все отправлено! Проверьте Sentry через 30 секунд');
            });
          };

          console.log('[Sentry] Доступна функция window.testSentry() для тестирования');
        }
      } catch (testError) {
        console.error('[Sentry] Ошибка при создании testSentry функции:', testError);
      }
    }
  } catch (error) {
    // Ошибки инициализации Sentry не должны ломать приложение
    console.error('[Sentry] Ошибка инициализации:', error);
  }
});
